import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { departamentoService, empresaService } from '@/services/api';
import { Departamento, Empresa } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, FolderTree, Edit, Trash2, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';

const PAGE_SIZE = 10;

export default function Departamentos() {
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  // Evita o segundo clique em Criar/Salvar enquanto o primeiro ainda está gravando.
  const [salvando, setSalvando] = useState(false);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalDepartamentos, setTotalDepartamentos] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const isSuperAdmin = user?.tipo === 'superadmin';
  // Empresa filtrada vem do seletor global do header: pro superadmin é o
  // `empresaFiltroId` (pode ser "todas as empresas"); pra admin/comum é a empresa vinculada.
  const selectedEmpresa = (isSuperAdmin ? empresaFiltroId : empresaAtiva?.id) ?? (isSuperAdmin ? 'all' : '');

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDep, setEditingDep] = useState<Departamento | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form state
  const [nome, setNome] = useState('');
  const [nomeInitial, setNomeInitial] = useState('');
  
  // Unsaved changes state
  const [showUnsavedDep, setShowUnsavedDep] = useState(false);

  const hasDepChanges = useCallback(() => {
    return nome !== nomeInitial;
  }, [nome, nomeInitial]);

  const loadEmpresas = async () => {
    try {
      const result = await empresaService.getAll();
      if (result.success) {
        setEmpresas(result.data.filter(e => e.status === 'ativa'));
      }
    } catch (error) {
      console.error('Error loading empresas:', error);
    }
  };

  const loadData = async () => {
    // Departamentos exige uma empresa específica — "Todas as empresas" só existe em
    // Dashboard e Usuários. Sem empresa escolhida, nem tenta carregar.
    if (isSuperAdmin && selectedEmpresa === 'all') {
      setDepartamentos([]);
      setTotalDepartamentos(0);
      setTotalPages(0);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      let empresaId: string | undefined;
      if (isSuperAdmin) {
        empresaId = selectedEmpresa;
      } else {
        const activeId = empresaAtiva?.id;
        if (!activeId) return;
        empresaId = activeId;
      }

      const depResult = await departamentoService.getPaginated({
        page,
        limit: PAGE_SIZE,
        empresa_id: empresaId,
      });

      if (depResult.success) {
        setDepartamentos(depResult.data);
        setTotalDepartamentos(depResult.pagination.total);
        setTotalPages(depResult.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadEmpresas();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    setPage(1);
  }, [selectedEmpresa]);

  useEffect(() => {
    if (user?.tipo === 'admin' || user?.tipo === 'superadmin') {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [user, selectedEmpresa, page]);

  const handleSave = async () => {
    if (salvando) return;
    setSalvando(true);
    try {
      if (editingDep) {
        // Ao editar, usa a empresa do departamento que está sendo editado
        const result = await departamentoService.update(editingDep.id, { nome });
        if (result.success) {
          toast.success('Departamento atualizado');
          loadData();
          setIsDialogOpen(false);
          setNome('');
          setNomeInitial('');
          setEditingDep(null);
        } else {
          toast.error(result.error || 'Erro ao atualizar departamento');
        }
      } else {
        // Ao criar, precisa selecionar uma empresa (não pode ser 'all')
        const empresaId = isSuperAdmin 
          ? (selectedEmpresa === 'all' ? null : selectedEmpresa)
          : user?.empresa_id;

        // if (!empresaId) {
        //   toast.error('Selecione uma empresa para criar/editar o departamento');
        //   return;
        // }

        const result = await departamentoService.create({
          nome,
          empresa_id: empresaId,
        });
        if (result.success) {
          toast.success('Departamento criado');
          loadData();
          setIsDialogOpen(false);
          setNome('');
          setNomeInitial('');
          setEditingDep(null);
        } else {
          toast.error(result.error || 'Erro ao criar departamento');
        }
      }
    } catch (error) {
      toast.error('Erro ao salvar departamento');
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;

    try {
      const result = await departamentoService.delete(deleteConfirmId);
      if (result.success) {
        toast.success('Departamento excluído');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao excluir departamento');
      }
    } catch (error) {
      toast.error('Erro ao excluir departamento');
    }
    setDeleteConfirmId(null);
  };

  const openEdit = (dep: Departamento) => {
    setEditingDep(dep);
    setNome(dep.nome);
    setNomeInitial(dep.nome);
    setIsDialogOpen(true);
  };

  const openNewDep = () => {
    setNome('');
    setNomeInitial('');
    setEditingDep(null);
    setIsDialogOpen(true);
  };

  const getDepartamentoProgress = (departamentoId: string) => {
    const departamento = departamentos.find((dep) => dep.id === departamentoId);
    return departamento?.progresso_medio ?? 0;
  };

  const handleCloseDepDialog = useCallback(() => {
    if (hasDepChanges()) {
      setShowUnsavedDep(true);
    } else {
      setIsDialogOpen(false);
      setNome('');
      setNomeInitial('');
      setEditingDep(null);
    }
  }, [hasDepChanges]);

  // Only admin and superadmin can access this page
  if (user?.tipo !== 'admin' && user?.tipo !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  if (isSuperAdmin && selectedEmpresa === 'all') {
    return (
      <AppLayout>
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border border-dashed bg-muted/20 p-8 text-center">
          <h2 className="text-lg font-semibold">Selecione uma empresa</h2>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            Departamentos é por empresa — escolha uma empresa específica no filtro do topo (está em "Todas as empresas").
          </p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="rounded-lg border bg-card p-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Departamentos</h1>
            <p className="text-muted-foreground">
              {isSuperAdmin 
                ? 'Gerencie os departamentos das empresas' 
                : 'Gerencie os departamentos da sua empresa'}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={openNewDep} data-tour="novo-departamento">
              <Plus className="h-4 w-4 mr-2" />
              Novo Departamento
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b p-4">
              <span className="text-sm text-muted-foreground">
                {departamentos.length} de {totalDepartamentos} departamento(s)
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  {isSuperAdmin && <TableHead>Empresa</TableHead>}
                  <TableHead>Departamento</TableHead>
                  <TableHead>Progresso Médio</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departamentos.map((dep) => {
                  const empresa = empresas.find(e => e.id === dep.empresa_id);
                  const progressoMedio = getDepartamentoProgress(dep.id);
                  const okrCount = dep.okr_count ?? 0;
                  return (
                    <TableRow key={dep.id}>
                      {isSuperAdmin && (
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {empresa?.nome || 'N/A'}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FolderTree className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{dep.nome}</span>
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[220px]">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span>{progressoMedio}%</span>
                            <span>{okrCount} OKRs</span>
                          </div>
                          <Progress value={progressoMedio} className="h-2" />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(dep)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirmId(dep.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {departamentos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 4 : 3} className="text-center py-8 text-muted-foreground">
                      Nenhum departamento cadastrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-sm text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    title="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    title="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDepDialog();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingDep ? 'Editar Departamento' : 'Novo Departamento'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dep_nome">Nome do Departamento</Label>
              <Input
                id="dep_nome"
                value={nome}
                // Padrão do cadastro: TUDO MAIÚSCULO. O backend grava assim; aqui é só visual (CSS), para o cursor
                // não pular para o fim ao editar no meio do nome.
                className="uppercase"
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: ENGENHARIA"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseDepDialog}
            >
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!nome.trim() || salvando}>
              {editingDep ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Dep Confirmation */}
      <UnsavedChangesDialog
        open={showUnsavedDep}
        onOpenChange={setShowUnsavedDep}
        onConfirm={() => {
          setShowUnsavedDep(false);
          setIsDialogOpen(false);
          setNome('');
          setNomeInitial('');
          setEditingDep(null);
        }}
        onCancel={() => setShowUnsavedDep(false)}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este departamento? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
